import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from '../useFetch';

/**
 * Urkundenerzeugung aus einem Vorgang (#1245).
 *
 * Der produktive Weg, nicht der Verwaltungsweg: der Mitarbeiter sieht im Vorgang,
 * welche Entscheidungsbäume für seine Rechtsgeschäftsart freigegeben sind, wählt
 * einen und übergibt an Word. Von ArnoTop, Registern, DOCVARIABLEs und Mappings
 * kommt hier nichts vor — das ist Sache der Verwaltung.
 *
 * Beim Start entsteht ein Lauf, und der schreibt die verwendete Version fest.
 * Wird morgen Version 3 veröffentlicht, bleibt die heute begonnene Urkunde bei
 * Version 2. Deshalb wird der Lauf im Backend angelegt und nicht im Browser
 * zusammengesetzt.
 */

export type DeedSourceType = 'ARNOTOP_IMPORTED' | 'DINO_NATIVE';

export type DeedRunStatus = 'OPEN' | 'CLAIMED' | 'INSERTED' | 'CANCELLED';

/** Warum ein Baum nicht angeboten wird (nur mit `includeUnavailable`). */
export type DeedUnavailableReason =
  | 'NO_PUBLISHED_VERSION'
  | 'NOT_ACTIVE'
  | 'VALIDATION_FAILED'
  | 'ASSIGNMENT_DISABLED';

export interface IDeedTemplate {
  idDecisionTemplate: number;
  templateId: string;
  name: string;
  description: string | null;
  group: string;
  sourceType: DeedSourceType;
  isNative: boolean;
  lifecycleStatus: string;
  /** Die Version, mit der gestartet würde — festgeschrieben beim Anlegen des Laufs. */
  idDecisionTemplateVersion: number | null;
  versionNumber: number | null;
  isDefault: boolean;
  isEnabled: boolean;
  sortOrder: number;
  /** Nur bei importierten Bäumen: mGB/oGB. Native Bäume kennen das nicht. */
  variants: string[];
  available: boolean;
  unavailableReason: DeedUnavailableReason | null;
}

export interface IDeedOffer {
  idLegalTransaction: number;
  title: string | null;
  idLegalTransactionType: number | null;
  legalTransactionType: string | null;
  templates: IDeedTemplate[];
  defaultTemplateId: number | null;
  count: number;
}

export interface IDeedRun {
  idDecisionDocumentRun: number;
  handoffCode: string;
  idLegalTransaction: number;
  idDecisionTemplate: number;
  idDecisionTemplateVersion: number;
  templateId: string;
  templateName: string;
  description: string | null;
  group: string;
  versionNumber: number;
  sourceType: DeedSourceType;
  variant: string | null;
  status: DeedRunStatus;
  fingerprint: string | null;
  sectionsInserted: number;
  createdAt: string | null;
  claimedAt: string | null;
  completedAt: string | null;
  legalTransactionTitle?: string | null;
}

/**
 * Was dieser Vorgang zur Urkundenerzeugung anbietet.
 *
 * Bewusst eine kleine Abfrage: Freigaben und Versionsköpfe, keine Bausteine. Die
 * Runtime wird erst geladen, wenn ein Baum gewählt ist — und dann in Word.
 */
export const useDeedTemplates = (idLegalTransaction?: number | null) => {
  const { data, isLoading, refetch } = useQuery<IDeedOffer>({
    refetchOnWindowFocus: false,
    enabled: Boolean(idLegalTransaction),
    queryKey: ['arno/deed-templates', idLegalTransaction],
    queryFn: () =>
      customFetcher({
        endpoint: `/arno/legal-transactions/${idLegalTransaction}/decision-templates`,
      }),
  });

  return {
    deedOffer: data,
    isLoadingDeedOffer: isLoading,
    refetchDeedOffer: refetch,
  };
};

/** Womit für diesen Vorgang schon Urkunden erzeugt wurden. */
export const useDeedRunsOfLegalTransaction = (
  idLegalTransaction?: number | null
) => {
  const { data, isLoading } = useQuery<IDeedRun[]>({
    refetchOnWindowFocus: false,
    enabled: Boolean(idLegalTransaction),
    queryKey: ['arno/deed-runs', idLegalTransaction],
    queryFn: () =>
      customFetcher({
        endpoint: `/arno/legal-transactions/${idLegalTransaction}/deed-runs`,
      }),
  });

  return { deedRuns: data ?? [], isLoadingDeedRuns: isLoading };
};

/**
 * Urkundenerzeugung starten.
 *
 * Das Ergebnis ist der Auftrag, den die Word-Taskpane abholt — mit der Version,
 * die ab jetzt für diese Urkunde gilt.
 */
export const useStartDeedRun = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: startDeedRun, isPending } = useMutation({
    mutationFn: (body: {
      idLegalTransaction: number;
      idDecisionTemplate: number;
      variant?: string;
    }): Promise<IDeedRun> =>
      customFetcher({ endpoint: '/arno/deed-runs', method: 'POST', body }),
    onSuccess: run => {
      queryClient.invalidateQueries({
        queryKey: ['arno/deed-runs', run.idLegalTransaction],
      });
    },
  });

  return { startDeedRun, isStartingDeedRun: isPending };
};

/**
 * »In Word öffnen« (#1245): ein leeres Word-Dokument, das die DiNo-Leiste beim Öffnen selbst
 * aufmacht und den Auftrag kennt. Den Text schreibt danach das Add-in.
 */
export const useDeedRunWordDocument = () => {
  const { mutateAsync: fetchDeedRunWordDocument, isPending } = useMutation({
    mutationFn: (
      idDecisionDocumentRun: number
    ): Promise<
      Pick<IDinoDeedFile, 'fileName' | 'mimeType' | 'size' | 'data'>
    > =>
      customFetcher({
        endpoint: `/arno/deed-runs/${idDecisionDocumentRun}/word-document`,
      }),
  });

  return {
    fetchDeedRunWordDocument,
    isFetchingDeedRunWordDocument: isPending,
  };
};

/** Auftrag verwerfen — der Benutzer hat die Urkunde doch nicht erzeugt. */
export const useCancelDeedRun = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: cancelDeedRun, isPending } = useMutation({
    mutationFn: (idDecisionDocumentRun: number): Promise<IDeedRun> =>
      customFetcher({
        endpoint: `/arno/deed-runs/${idDecisionDocumentRun}/cancel`,
        method: 'POST',
      }),
    onSuccess: run => {
      queryClient.invalidateQueries({
        queryKey: ['arno/deed-runs', run.idLegalTransaction],
      });
    },
  });

  return { cancelDeedRun, isCancellingDeedRun: isPending };
};

//////////////////// Urkunde ganz in DiNo (#1245, Teil 48)

/**
 * Ein Absatz oder eine Tabelle der Urkunde im Modell des Editors (`IRichParagraph`), dazu das
 * Absatzformat des Plans als `source` — es gilt in der Datei, solange niemand das jeweilige
 * Merkmal im Editor ändert.
 */
export type IDinoDeedItem = Record<string, unknown> & {
  kind: 'paragraph' | 'table';
};

export interface IDinoDeedSection {
  order: number;
  label: string;
  blockId: string;
  status: string;
  lists: Record<string, unknown>;
  styles: Record<string, unknown>;
  items: IDinoDeedItem[];
}

export interface IDinoDeedQuestion {
  kind: string;
  key: string;
  prompt: string;
  options?: { label: string; answer: string }[];
  answerType?: string;
  helpText?: string | null;
}

export interface IDinoDeedPreview {
  idLegalTransaction: number;
  legalTransactionTitle: string | null;
  idDecisionTemplate: number;
  templateName: string;
  idDecisionTemplateVersion: number;
  versionNumber: number;
  variant: string | null;
  plan: {
    pendingQuestions: IDinoDeedQuestion[];
    pendingCount: number;
    missingValues?: {
      label: string | null;
      selector: string | null;
      section: string | null;
      fieldName?: string;
    }[];
    fingerprint?: string;
  };
  sections: IDinoDeedSection[];
}

export interface IDinoDeedFile {
  fileName: string;
  idMIMEType: number | null;
  mimeType: string;
  size: number;
  data: string;
}

/** Die Urkunde zu Vorgang, Baum und Antworten — dieselbe Rechnung wie »In Word erstellen«. */
export const useDinoDeedPreview = () => {
  const { mutateAsync: previewDinoDeed, isPending } = useMutation({
    mutationFn: (body: {
      idLegalTransaction: number;
      idDecisionTemplate: number;
      variant?: string;
      answers: Record<string, string>;
    }): Promise<IDinoDeedPreview> =>
      customFetcher({
        endpoint: '/arno/dino-deeds/preview',
        method: 'POST',
        body,
      }),
  });
  return { previewDinoDeed, isPreviewingDinoDeed: isPending };
};

/** Die (bearbeitete) Urkunde als .docx. Gespeichert wird sie mit dem Vorgang, nicht hier. */
export const useDinoDeedDocx = () => {
  const { mutateAsync: createDinoDeedDocx, isPending } = useMutation({
    mutationFn: (body: {
      idLegalTransaction: number;
      idDecisionTemplate: number;
      sections: IDinoDeedSection[];
    }): Promise<IDinoDeedFile> =>
      customFetcher({
        endpoint: '/arno/dino-deeds/docx',
        method: 'POST',
        body,
      }),
  });
  return { createDinoDeedDocx, isCreatingDinoDeedDocx: isPending };
};
